<template>
  <div>
    <PageLoader ref="loader">
      <div>
        <div v-if="calledFrom === 'web'">
            <v-btn @click="closeMe()"> Return to Invoice Dashboard </v-btn>
        </div>
        <v-container fluid class="pt-3" style="max-width: 600px; padding: 0"
          v-if="showInvoicePaymentPage()"
        >
          <v-row justify="center">
            <v-card class="grey lighten-5" justify="center" elevation="12">
              <v-row justify="center" class="mt-8">
                <v-card-title justify="center"
                  class="blue--text lighten-2 display-1 font-weight-bold pa-0">
                    ${{ dueAmount | dollarsAndCents }}
                </v-card-title>
              </v-row>
              <v-row justify="center">
                <v-card-subtitle class="title" justify="center"> Balance Due </v-card-subtitle>
              </v-row>
              <v-row justify="center">
                <v-col cols="12" sm="5"
                  class="mx-auto d-flex justify-center flex-column align-center pa-0">
                  Invoice # {{invoiceNumber}}
                </v-col>
                <v-col
                  :class="!mobileView ? 'mt-5' : ''"
                  class="mx-auto d-flex justify-center flex-column align-center pa-0"
                  cols="12" sm="5" style="text-align:center;">
                  Invoice Date: {{issueDate}}<br>
                  Due Date: {{dueDate}}
                </v-col>
              </v-row>
              <v-divider class="mx-10 black" style="border-width: 2px !important;" />
              <v-row justify="center" class="mb-5">
                <v-col cols="10">
                  <h3> Billing Information </h3>
                </v-col>
              </v-row>
              <v-form
                class="not-signin"
                ref="form"
                lazy-validation
                @submit.prevent="submit"
              >
                  <v-row justify="center">
                    <v-col cols="10">
                      <v-text-field label="Company" v-model="invoiceCustomer.company"/>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="5">
                      <v-text-field label="First Name" v-model="invoiceCustomer.firstName"/>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field label="Last Name" v-model="invoiceCustomer.lastName"/>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="5">
                      <v-text-field label="Address 1" v-model="invoiceCustomer.address1"/>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field label="Address 2" v-model="invoiceCustomer.address2"/>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="5">
                      <v-text-field label="City" v-model="invoiceCustomer.city"/>
                    </v-col>
                    <v-col cols="5">
                      <v-select
                        label="State"
                        v-if="invoiceCustomer.country === 'US'"
                        v-model="invoiceCustomer.state"
                        :items="states"
                        :menu-props="{ auto: true }"
                      />
                      <v-text-field
                        v-else
                        v-model="invoiceCustomer.state"
                        label="State"
                      />
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="5">
                      <v-text-field label="Zip Code" v-model="invoiceCustomer.zip"/>
                    </v-col>
                    <v-col cols="5">
                      <v-select label="Country" v-model="invoiceCustomer.country"
                        :items="countries" item-text="name" item-value="alpha2Code"/>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="5">
                      <v-text-field label="Email Address" v-model="invoiceCustomer.email"/>
                    </v-col>
                    <v-col cols="5" />
                  </v-row>
                <template v-if="this.showCustomFields && this.hasCustomFields">
                  <v-row justify="center" class="mb-5">
                    <v-col cols="10">
                      <h3> Additional Information </h3>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col cols="10">
                      <PaymentCustomFields v-model="this.customFields" read-only/>
                    </v-col>
                  </v-row>
                </template>
                <v-row justify="center">
                  <v-col cols="10">
                    <HppCardForm :values="values" :isEditing="false"
                      :hasCreditCardProcessor="hasCreditCardProcessor"
                      :hasAchProcessor="hasAchProcessor" @robotCheck="robotCheck"
                      :surchargePercent="surchargePercent" :surchargeEnabled="surchargeEnabled"
                      :disableAmountEdit="!invoiceInformation.allowLessPayment"
                      :achAuthorization="achAuthorization"
                      @updateAchAuthorization="updateAchAuthorization"
                      @updateIsAchTransaction="updateIsAchTransaction"
                    />
                  </v-col>
                </v-row>
                <v-row class="mt-9" justify="center">
                  <v-btn :disabled="isAchTransaction && !achAuthorization"
                      class="mb-9" color="secondary" type="submit">
                    Submit payment
                  </v-btn>
                </v-row>
              </v-form>
            </v-card>
          </v-row>
        </v-container>
        <div v-else>
          <InvoicePaid
            :invoiceNumber="invoiceNumber"
          />
        </div>
      </div>
    </PageLoader>
    <PaymentDetailsModal
      ref="paymentDetailsModal"
      :response="txnResponse"
      @closed="resetResponse"
    />
    <LoadingModal loadingText="Processing payment..." :showModal="isSubmitting" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import HppCardForm from '@/merchant/views/hosted-payments/design-components/HppCardForm.vue';
import PageLoader from '@/common/components/PageLoader.vue';
import getDomain from '@/common/domain';
import { JsonApiSingleResponse } from '@/jsonApi.types';
import { initHppStates } from '@/common/util/hostedPaymentPages.util';
import axios from 'axios';
import getSubdomain from '@/common/context';
import LoadingModal from '@/common/components/LoadingModal.vue';
import { Transaction, TransactionStatus } from '@/common/types/transactions.types';
import PaymentCustomFields from '@/merchant/views/components/customer-payment/PaymentCustomFields.vue';
import PaymentDetailsModal from '@/common/components/payments/PaymentDetailsModal.vue';
import { isEnabled } from 'vue-feature-flipping';
import { Feature } from '@/features.types';
import { reformatCustomFields } from '@/common/util/payments.util';
import { decodeCustomFields } from '@/common/types/invoice.types';
import InvoicePaid from './InvoicePaid.vue';
import { InvoiceCustomer, InvoiceInformationInterface } from './invoices.types';


export default Vue.extend({
  props: {
    id: {
      type: String,
      required: true,
    },
    calledFrom: {
      type: String,
      default: '',
      required: false,
    },
  },
  mounted() {
    axios
      .get(`${getDomain('api')}/countries`).then(({ data: { data: countries } }) => {
        this.countries = countries;
      });
    this.getInvoice();
  },
  data() {
    return {
      values: {
        amount: '' as string,
        type: '',
        creditCard: {
          cardNumber: '',
          expMonth: '',
          expYear: '',
          cvv: '',
        },
        ach: {
          accountHolderType: 'personal',
          accountType: 'checking',
          accountNumber: '',
          routingNumber: '',
        },
      } as any,
      invoiceCustomer: {} as InvoiceCustomer,
      states: initHppStates(),
      dueAmount: 0 as number,
      amountPaid: 0 as number,
      invoiceNumber: '' as string,
      issueDate: '' as string,
      dueDate: '' as string,
      invoiceInformation: {} as InvoiceInformationInterface,
      creditCardProcessor: '' as string,
      achProcessor: '' as string,
      countries: [],
      surchargePercent: 0 as number,
      surchargeEnabled: false as boolean,
      invoiceId: '' as string,
      token: '' as string,
      isSubmitting: false as boolean,
      txnResponse: null as JsonApiSingleResponse<any> | null,
      reCaptcha: false as boolean,
      invoiceStatus: '' as string,
      customFields: [],
      achAuthorization: false,
      isAchTransaction: false,
    };
  },
  methods: {
    submit() {
      if (!this.isValid || !this.reCaptcha) {
        return;
      }
      this.isSubmitting = true;
      const payload: any = {
        amount: this.values.amount,
        description: 'Invoice payment',
        paymentMethod: {
          type: this.values.type,
          creditCard: this.values.creditCard,
          ach: this.values.ach,
        },
      };
      if (this.values.type === 'credit') {
        payload.paymentMethod.creditCard.method = 'capture';
        payload.paymentMethod.processorId = this.creditCardProcessor;
      } else {
        payload.paymentMethod.processorId = this.achProcessor;
      }
      payload.customer = {
        billingAddress: this.invoiceCustomer,
      };
      payload.customFields = reformatCustomFields(this.customFields, true);
      payload.transactionType = 'moto';
      payload.internalNote = '';
      payload.isLevel3Transaction = false;
      payload.invoiceId = this.invoiceId;
      payload.dueAmount = this.dueAmount;
      payload.amountPaid = this.amountPaid;
      payload.invoiceUuid = this.id;
      payload.achAuthorization = this.achAuthorization;
      if (!this.invoiceInformation.allowLessPayment && payload.amount < payload.dueAmount) {
        this.isSubmitting = false;
        this.$toasted.error(`The amount should not be less than the due amount $${(this.$options.filters as any).dollarsAndCents(payload.dueAmount)}`);
        return;
      }
      axios.post(`${getDomain('api')}/transactions`, payload, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.token}`,
          'x-merchant-proxy': getSubdomain(),
        },
      }).then(({ data }: { data: JsonApiSingleResponse<Transaction> }) => {
        this.isSubmitting = false;
        this.txnResponse = data;
        (this.$refs.paymentDetailsModal as any).setVisibility(true);
        this.$toasted.success('Transaction processed!');
      }).catch((this as any).handleTransactionError);
    },
    handleTransactionError({ response }: any) {
      this.submitActions(false);
      this.txnResponse = response;
      (this.$refs.paymentDetailsModal as any).setVisibility(true);
    },
    submitActions(isSubmitting: boolean) {
      this.isSubmitting = isSubmitting;
    },
    getInvoice() {
      axios
        .get(`${getDomain('api')}/invoice/${this.id}/pay`, {
          params: {
            subdomain: getSubdomain(),
          },
        }).then(this.setInvoice)
        .catch((err) => {
          console.log(err);
          if (this.$refs.loader) {
            (this.$refs.loader as any).setError();
          }
        });
    },
    setInvoice({ data }: { data: JsonApiSingleResponse<any> }) {
      this.values!.amount = data.data.amountDue;
      this.values.type = 'credit';
      this.invoiceCustomer = data.data.invoiceCustomer;
      this.dueAmount = data.data.amountDue;
      this.amountPaid = data.data.amountPaid;
      this.invoiceNumber = data.data.invoiceNumber;
      this.issueDate = data.data.invoiceInformation.issueDate;
      this.dueDate = data.data.invoiceInformation.dueDateFormatted;
      this.invoiceInformation = data.data.invoiceInformation;
      this.creditCardProcessor = data.data
        .invoiceInformation.processorInformation.creditCardProcessorId;
      this.achProcessor = data.data.invoiceInformation.processorInformation.achProcessorId;
      this.invoiceInformation = data.data.invoiceInformation;
      this.surchargePercent = data.data.surchargePercent;
      this.surchargeEnabled = data.data.surchargeEnabled;
      this.token = data.data.token;
      this.invoiceId = data.data.invoiceId;
      this.invoiceStatus = data.data.status;
      this.customFields = decodeCustomFields(data.data?.customFields ?? []);
      (this.$refs.loader as any).setReady();
    },
    resetResponse() {
      if ([
        TransactionStatus.CAPTURED,
        TransactionStatus.AUTHORIZED,
      ].includes(this.txnResponse?.data.status)) {
        if (this.calledFrom === 'web') {
          window.close();
        } else {
          this.$router.go(0);
        }
      }
      this.txnResponse = null;
    },
    robotCheck(robot: boolean) {
      this.reCaptcha = robot;
    },
    closeMe() {
      window.close();
    },
    showInvoicePaymentPage() {
      if (['Closed', 'Paid'].includes(this.invoiceStatus)) {
        return false;
      } return true;
    },
    updateAchAuthorization(value: boolean) {
      this.achAuthorization = value;
    },
    updateIsAchTransaction(value: boolean) {
      this.isAchTransaction = value;
    },
  },
  computed: {
    hasCreditCardProcessor():boolean {
      if (this.creditCardProcessor) {
        return true;
      } return false;
    },
    hasAchProcessor():boolean {
      if (this.achProcessor) {
        return true;
      } return false;
    },
    mobileView(): boolean {
      return this.$vuetify.breakpoint.name === 'xs';
    },
    isValid(): boolean {
      return ((this as any).$refs.form as any).validate();
    },
    showCustomFields(): boolean {
      return isEnabled(Feature.CUSTOM_FIELDS);
    },
    hasCustomFields(): boolean {
      return this.customFields.length > 0;
    },
  },
  components: {
    HppCardForm,
    PageLoader,
    LoadingModal,
    PaymentCustomFields,
    PaymentDetailsModal,
    InvoicePaid,
  },
});
</script>

<style scoped>
  ::v-deep .v-text-field__prefix {
    color: red;
  }

  ::v-deep .mdi-asterisk::before {
    color: red;
    font-size: 7px;
  }

  .col {
    padding-top: 0px;
    padding-bottom: 0px;
  }
</style>
